import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { getUser, updateProfile, updateAvatar } from '../services/api';

const Profile = () => {
    const [user, setUser] = useState(null);
    const [formData, setFormData] = useState({ name: '', phone_number: '', cnic: '', address: '' });
    
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    
    const avatarInputRef = useRef(null);

    useEffect(() => {
        fetchUser();
    }, []);

    const fetchUser = async () => {
        try {
            const response = await getUser();
            setUser(response.data);
            setFormData({
                name: response.data.name,
                phone_number: response.data.phone_number || '',
                cnic: response.data.cnic || '',
                address: response.data.address || ''
            });
        } catch (err) {
            setError('Failed to fetch user data.');
        } finally {
            setLoading(false);
        }
    };

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');
        setSaving(true);
        try {
            const res = await updateProfile(formData);
            const updatedUser = res.data.user;
            setUser(updatedUser); 
            localStorage.setItem('user', JSON.stringify(updatedUser));
            window.dispatchEvent(new Event('localStorageChanged'));
            setMessage('Identity updated successfully!');
            setTimeout(() => setMessage(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update profile.');
        } finally {
            setSaving(false);
        }
    };

    const handleAvatarChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const avatarFormData = new FormData();
        avatarFormData.append('avatar', file);

        try {
            const res = await updateAvatar(avatarFormData);
            const updatedUser = res.data.user;
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
            window.dispatchEvent(new Event('localStorageChanged'));
            setMessage('Avatar updated successfully!');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update avatar.');
        }
    };

    if (loading) {
        return (
            <Layout title="My Profile">
                <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error && !user) {
        return <Layout title="My Profile"><div className="alert alert-danger">{error}</div></Layout>;
    }

    // Use the backend-provided full URL or a fallback
    const avatarUrl = user.profile_photo_url || `https://ui-avatars.com/api/?name=${user.name}&background=random`;

    return (
        <Layout title="Workforce Identity">
            <div className="profile-workspace animate__animated animate__fadeIn">
                {message && <div className="alert alert-success border-0 shadow-sm rounded-4 mb-4"><i className="bi bi-check-circle-fill me-2"></i>{message}</div>}
                {error && <div className="alert alert-danger border-0 shadow-sm rounded-4 mb-4"><i className="bi bi-exclamation-triangle-fill me-2"></i>{error}</div>}
                
                {/* Profile Hero Section */}
                <div className="profile-hero-card mb-4 overflow-hidden position-relative">
                    <div className="hero-banner-gradient"></div>
                    <div className="card-body p-4 p-lg-5 position-relative">
                        <div className="row align-items-center">
                            <div className="col-auto">
                                <div className="avatar-edit-wrapper position-relative">
                                    <div className="avatar-main-container shadow-lg">
                                        <img 
                                            src={avatarUrl}
                                            alt="Avatar" 
                                            className="profile-avatar-img"
                                        />
                                    </div>
                                    <button 
                                        className="btn btn-avatar-upload shadow-sm" 
                                        onClick={() => avatarInputRef.current.click()}
                                        title="Update Personnel Photo"
                                    >
                                        <i className="bi bi-camera-fill"></i>
                                    </button>
                                    <input type="file" ref={avatarInputRef} onChange={handleAvatarChange} className="d-none" accept="image/*" />
                                </div>
                            </div>
                            <div className="col mt-4 mt-md-0 ps-md-4">
                                <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
                                    <h2 className="fw-extrabold mb-0 text-white">{user.name}</h2>
                                    <span className="badge rounded-pill bg-dark bg-opacity-25 text-white border border-white border-opacity-25 px-3 py-2 fw-bold text-uppercase ls-1 small shadow-sm">
                                        <i className="bi bi-shield-check me-2"></i>{user.role}
                                    </span>
                                </div>
                                <div className="d-flex flex-wrap gap-4 text-white text-opacity-85">
                                    <div className="small fw-medium"><i className="bi bi-envelope-fill me-2"></i>{user.email}</div>
                                    <div className="small fw-medium"><i className="bi bi-calendar3 me-2"></i>Personnel since {new Date(user.created_at).toLocaleDateString()}</div>
                                </div>
                            </div>
                            <div className="col-lg-auto mt-4 mt-lg-0">
                                <div className="d-flex gap-2">
                                    <button 
                                        className="btn btn-glass-white px-4 fw-bold shadow-sm" 
                                        onClick={async () => {
                                            const btn = document.querySelector('.btn-glass-white');
                                            btn.innerHTML = '<i class="bi bi-arrow-clockwise spin me-2"></i>Syncing...';
                                            await fetchUser();
                                            setMessage('Data synchronized with server successfully!');
                                            btn.innerHTML = '<i class="bi bi-arrow-clockwise me-2"></i>Sync Data';
                                            setTimeout(() => setMessage(''), 3000);
                                        }}
                                    >
                                        <i className="bi bi-arrow-clockwise me-2"></i>Sync Data
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row g-4">
                    {/* Left Column: Personal Terminal */}
                    <div className="col-xl-8">
                        <div className="card border-0 shadow-sm rounded-4 h-100">
                            <div className="card-header bg-transparent border-0 p-4 pb-0">
                                <h5 className="fw-bold mb-0"><i className="bi bi-person-badge-fill me-2 text-primary"></i>Identity Terminal</h5>
                                <p className="text-muted small mb-0 mt-1">Manage your professional credentials and contact information.</p>
                            </div>
                            <div className="card-body p-4">
                                <form onSubmit={handleProfileUpdate}>
                                    <div className="row g-4">
                                        <div className="col-md-6">
                                            <div className="form-group-modern">
                                                <label className="form-label-modern">Full Legal Name</label>
                                                <div className="input-modern-wrapper">
                                                    <i className="bi bi-person"></i>
                                                    <input type="text" name="name" className="form-control-modern" value={formData.name} onChange={handleFormChange} placeholder="Enter your full name" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group-modern">
                                                <label className="form-label-modern">Authorized Email</label>
                                                <div className="input-modern-wrapper bg-light-subtle opacity-75">
                                                    <i className="bi bi-envelope"></i>
                                                    <input type="email" className="form-control-modern" value={user?.email || ''} disabled />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group-modern">
                                                <label className="form-label-modern">Contact Trace (Phone)</label>
                                                <div className="input-modern-wrapper">
                                                    <i className="bi bi-phone"></i>
                                                    <input type="text" name="phone_number" className="form-control-modern" value={formData.phone_number} onChange={handleFormChange} placeholder="+92 3XX XXXXXXX" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group-modern">
                                                <label className="form-label-modern">Government ID (CNIC)</label>
                                                <div className="input-modern-wrapper">
                                                    <i className="bi bi-card-text"></i>
                                                    <input type="text" name="cnic" className="form-control-modern" value={formData.cnic} onChange={handleFormChange} placeholder="42XXX-XXXXXXX-X" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="form-group-modern">
                                                <label className="form-label-modern">Registered Residential Address</label>
                                                <div className="input-modern-wrapper align-items-start pt-3" style={{ height: '140px' }}>
                                                    <i className="bi bi-geo-alt mt-1"></i>
                                                    <textarea 
                                                        name="address" 
                                                        className="form-control-modern" 
                                                        rows="3" 
                                                        value={formData.address} 
                                                        onChange={handleFormChange}
                                                        placeholder="Enter your complete home address"
                                                        style={{ height: '120px', resize: 'none' }}
                                                    ></textarea>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-5 pt-3 border-top d-flex flex-wrap gap-3 align-items-center justify-content-between">
                                        <div className="small text-muted">
                                            <i className="bi bi-info-circle me-2"></i>
                                            Last identification sync: {new Date().toLocaleTimeString()}
                                        </div>
                                        <button type="submit" className="btn btn-primary btn-lg rounded-pill px-5 fw-bold shadow-sm h-56px btn-commit-identity" disabled={saving}>
                                            {saving ? (
                                                <><span className="spinner-border spinner-border-sm me-2"></span>Committing...</>
                                            ) : (
                                                <><i className="bi bi-check2-circle me-2"></i>Commit Identity Changes</>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Analytics & Quick Stats */}
                    <div className="col-xl-4">
                        <div className="row g-4">
                            <div className="col-12">
                                <div className="card border-0 shadow-sm rounded-4 text-white" style={{ background: 'linear-gradient(135deg, var(--primary-color) 0%, #8B78E6 100%)' }}>
                                    <div className="card-body p-4">
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <div className="bg-white rounded-3 p-2 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '45px', height: '45px' }}>
                                                <i className="bi bi-bar-chart-line-fill fs-4" style={{ color: 'var(--primary-color)' }}></i>
                                            </div>
                                            <span className="small fw-bold ls-1 text-uppercase text-white">Workforce Impact</span>
                                        </div>
                                        <h2 className="fw-extrabold mb-1 text-white">Personnel Stats</h2>
                                        <p className="small opacity-75">Visualizing your operational performance.</p>
                                        
                                        <div className="mt-4 space-y-3">
                                            <div className="d-flex justify-content-between align-items-center p-3 bg-white bg-opacity-10 rounded-3 border border-white border-opacity-10">
                                                <span className="small fw-bold">Operations Handled</span>
                                                <span className="fw-extrabold">Active</span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center p-3 bg-white bg-opacity-10 rounded-3 border border-white border-opacity-10">
                                                <span className="small fw-bold">System Reliability</span>
                                                <span className="fw-extrabold">100%</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="card border-0 shadow-sm rounded-4">
                                    <div className="card-header bg-transparent border-0 p-4 pb-0">
                                        <h5 className="fw-bold mb-0">System Authority</h5>
                                    </div>
                                    <div className="card-body p-4">
                                        <div className="p-3 bg-light rounded-4 mb-3 border border-2 border-transparent transition-all">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="icon-badge-modern bg-warning bg-opacity-10 text-warning">
                                                    <i className="bi bi-key-fill"></i>
                                                </div>
                                                <div>
                                                    <div className="fw-bold text-dark small">Encrypted Session</div>
                                                    <div className="x-small text-muted">AES-256 Protected</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="p-3 bg-light rounded-4 border border-2 border-transparent transition-all">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="icon-badge-modern bg-info bg-opacity-10 text-info">
                                                    <i className="bi bi-shield-lock-fill"></i>
                                                </div>
                                                <div>
                                                    <div className="fw-bold text-dark small">Biometric Status</div>
                                                    <div className="x-small text-muted">Not Configured</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .profile-hero-card {
                    border: none;
                    border-radius: 30px;
                    background: var(--card-bg);
                    box-shadow: var(--card-shadow);
                }
                .hero-banner-gradient {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 100%;
                    background: linear-gradient(135deg, var(--primary-color) 0%, #8B78E6 100%);
                    opacity: 0.95;
                }
                .avatar-main-container {
                    width: 140px;
                    height: 140px;
                    border-radius: 40px;
                    overflow: hidden;
                    border: 5px solid rgba(255,255,255,0.2);
                    background: white;
                }
                .profile-avatar-img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .btn-avatar-upload {
                    position: absolute;
                    bottom: -10px;
                    right: -10px;
                    width: 45px;
                    height: 45px;
                    border-radius: 15px;
                    background: white;
                    color: var(--primary-color);
                    border: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.2rem;
                    transition: all 0.3s ease;
                }
                .btn-avatar-upload:hover {
                    transform: scale(1.1) rotate(15deg);
                    background: var(--primary-color);
                    color: white;
                }
                .btn-glass-white {
                    background: rgba(255,255,255,0.15);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255,255,255,0.2);
                    color: white;
                    border-radius: 14px;
                    padding: 12px 20px;
                    transition: all 0.3s ease;
                }
                .btn-glass-white:hover {
                    background: rgba(255,255,255,0.25);
                    color: white;
                    transform: translateY(-2px);
                }
                .spin {
                    animation: rotate 1s linear infinite;
                }
                @keyframes rotate {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .form-group-modern {
                    margin-bottom: 0;
                }
                .form-label-modern {
                    font-size: 0.75rem;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    color: var(--text-muted-color);
                    margin-bottom: 10px;
                    margin-left: 5px;
                }
                .input-modern-wrapper {
                    background: var(--bg-color);
                    border: 2px solid var(--border-color);
                    border-radius: 18px;
                    padding: 0 20px;
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    transition: all 0.3s ease;
                    height: 56px;
                }
                .input-modern-wrapper i {
                    color: var(--primary-color);
                    font-size: 1.1rem;
                    opacity: 0.7;
                }
                .form-control-modern {
                    background: transparent !important;
                    border: none !important;
                    box-shadow: none !important;
                    padding: 0 !important;
                    height: 100% !important;
                    font-weight: 600;
                    color: var(--text-color);
                    width: 100%;
                }
                .input-modern-wrapper:focus-within {
                    border-color: var(--primary-color);
                    background: var(--card-bg);
                    box-shadow: 0 10px 20px rgba(108, 93, 211, 0.08);
                }
                .icon-badge-modern {
                    width: 42px;
                    height: 42px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.1rem;
                }
                .space-y-3 > * + * {
                    margin-top: 1rem;
                }
                .h-56px {
                    height: 56px;
                }
                
                /* Dark Mode Overrides */
                [data-bs-theme="dark"] .input-modern-wrapper {
                    background: rgba(255,255,255,0.05);
                    border-color: rgba(255,255,255,0.1);
                }
                [data-bs-theme="dark"] .input-modern-wrapper:focus-within {
                    border-color: var(--primary-color);
                    background: rgba(255,255,255,0.08);
                }
                [data-bs-theme="dark"] .form-control-modern {
                    color: #EAEAF5 !important;
                }
                [data-bs-theme="dark"] .bg-light {
                    background-color: rgba(255,255,255,0.03) !important;
                }
                [data-bs-theme="dark"] .card {
                    background: #1A1A2E;
                    border-color: rgba(255,255,255,0.05);
                }
                [data-bs-theme="dark"] .text-dark {
                    color: #EAEAF5 !important;
                }
                [data-bs-theme="dark"] .form-label-modern {
                    color: #A3AED0;
                }
                @media (max-width: 768px) {
                    .profile-hero-card {
                        border-radius: 20px;
                    }
                    .avatar-main-container {
                        width: 100px;
                        height: 100px;
                        border-radius: 25px;
                    }
                    .btn-avatar-upload {
                        width: 35px;
                        height: 35px;
                        font-size: 1rem;
                    }
                    .btn-commit-identity {
                        width: 100% !important;
                        font-size: 0.85rem !important;
                        padding-left: 20px !important;
                        padding-right: 20px !important;
                    }
                    .hero-banner-gradient {
                        opacity: 1;
                    }
                }
            `}</style>
        </Layout>
    );
};

export default Profile;
